import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import {
  useChatsPageGetChatNotificationPreferencesV2Query,
  useChatsPageUpdateChatNotificationPreferencesV2Mutation,
  ChatsPageGetChatNotificationPreferencesV2Document,
} from '../../../generated';

interface UseChatNotificationPreferencesConfig {
  chatId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useChatNotificationPreferences = ({
  chatId,
  onSuccess,
  onError,
}: UseChatNotificationPreferencesConfig) => {
  const { addNotification } = useNotification();

  // Query for getting current V2 preferences
  const { data, loading, error, refetch } =
    useChatsPageGetChatNotificationPreferencesV2Query({
      variables: { chatId },
      skip: !chatId,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    });

  // Mutation for updating preferences (V2 — non-destructive merge handled by caller)
  const [updatePreferences, { loading: isUpdating }] =
    useChatsPageUpdateChatNotificationPreferencesV2Mutation({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

  /**
   * Writes the full deselectedUsersList.  Callers must merge before calling:
   *   - to enable:  remove supplierId from current list
   *   - to disable: add supplierId to current list
   * Current list is available via `notificationPreferences.newOrder.deselectedUsersList`.
   */
  const updateNotificationPreferences = useCallback(
    async (deselectedUsersList: string[]) => {
      try {
        await updatePreferences({
          variables: {
            input: {
              id: chatId,
              newOrder: {
                deselectedUsersList,
              },
            },
          },
          // Refetch the V2 query so the toggle reflects server state
          refetchQueries: [
            {
              query: ChatsPageGetChatNotificationPreferencesV2Document,
              variables: { chatId },
            },
          ],
        });

        addNotification({
          title: 'Success',
          message: 'Notification preferences updated successfully',
          type: 'success',
        });

        onSuccess?.();
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to update notification preferences';

        addNotification({
          title: 'Error',
          message: errorMessage,
          type: 'error',
        });

        onError?.(err instanceof Error ? err : new Error(errorMessage));
      }
    },
    [chatId, updatePreferences, addNotification, onSuccess, onError]
  );

  return {
    notificationPreferences: data?.chatNotificationPreferencesV2,
    isLoading: loading,
    isUpdating,
    error,
    updateNotificationPreferences,
    refetch,
  };
};
